import { eventTypes } from "@/shared/selectors/EventTypeSelector";
import { TimeService } from "@dexteel/mesf-core";
import moment from "moment-timezone";
import { EventYear } from "../models/EventYear";

export const MONTHS_LABELS = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];
export const MAX_SECTIONS_PER_CELL = 3;

const eventTypesByCode = new Map(eventTypes.map((t) => [t.code, t]));

export interface DayCellItem {
	key: string;
	eventId: number;
	displayName: string;
	fullName: string;
	color?: string;
	familyId: number | null;
	familyName: string | null;
	eventTypeCode: string;
	start: Date;
	end: Date;
	statusCode: string | null;
	scheduledFinishedTons: number | null;
	actualFinishedTons: number | null;
	scheduledChargedTons: number | null;
	actualChargedTons: number | null;
	scheduledRolledTons: number | null;
	actualRolledTons: number | null;
	scheduledRollingHours: number | null;
	actualRollingHours: number | null;
	billetStatusCode: string | null;
}

export interface DayCell {
	sections: DayCellItem[];
	overflowSections: Omit<DayCellItem, "color">[];
}

const buildItem = (event: EventYear): DayCellItem => {
	const base = {
		eventId: event.EventId,
		start: event.Start,
		end: event.End,
		statusCode: event.StatusCode,
		scheduledFinishedTons: event.ScheduledFinishedTons,
		actualFinishedTons: event.ActualFinishedTons,
		scheduledChargedTons: event.ScheduledChargedTons,
		actualChargedTons: event.ActualChargedTons,
		scheduledRolledTons: event.ScheduledRolledTons,
		actualRolledTons: event.ActualRolledTons,
		scheduledRollingHours: event.ScheduledRollingHours,
		actualRollingHours: event.ActualRollingHours,
	};
	if (event.EventTypeCode === "P") {
		const name = event.SectionName ?? `Section ${event.SectionId ?? ""}`;
		return {
			key: `P-${event.SectionId ?? event.EventId}`,
			displayName: name,
			fullName: name,
			color: event.SectionFamilyColor ?? undefined,
			familyId: event.SectionFamilyId,
			familyName: event.SectionFamilyName ?? null,
			eventTypeCode: "P",
			billetStatusCode: event.BilletsStatusCode ?? null,
			...base,
		};
	}
	const eventType = eventTypesByCode.get(event.EventTypeCode)!;
	return {
		key: `${event.EventTypeCode}-${event.EventId}`,
		displayName: eventType!.abbreviatedName,
		fullName: eventType!.name,
		color: eventType!.backgroundColor,
		familyId: null,
		familyName: null,
		eventTypeCode: event.EventTypeCode,
		billetStatusCode: null,
		...base,
	};
};

type EventCoverage = {
	event: EventYear;
	item: DayCellItem;
	dayKeys: Array<{ m: number; d: number; fullDay: boolean }>;
};

const expandEvent = (
	event: EventYear,
	year: number,
	tz: string,
): EventCoverage => {
	const start = moment.tz(event.Start, tz);
	const end = moment.tz(event.End, tz);
	const cursor = start.clone().startOf("day");
	const last = end.clone().startOf("day");
	const dayKeys: EventCoverage["dayKeys"] = [];

	while (cursor.isSameOrBefore(last)) {
		if (cursor.year() === year) {
			const dayStart = cursor.clone().startOf("day");
			const dayEnd = cursor.clone().endOf("day");
			dayKeys.push({
				m: cursor.month(),
				d: cursor.date() - 1,
				fullDay: start.isSameOrBefore(dayStart) && end.isSameOrAfter(dayEnd),
			});
		}
		cursor.add(1, "day");
	}

	return { event, item: buildItem(event), dayKeys };
};

export const daysInMonth = (year: number, monthIndex: number): number =>
	new Date(year, monthIndex + 1, 0).getDate();

export const buildYearGrid = (
	events: EventYear[],
	year: number,
): DayCell[][] => {
	const tz = TimeService.getInstance().getServerTimeZone();
	const grid: DayCell[][] = Array.from({ length: 12 }, () =>
		Array.from({ length: 31 }, () => ({ sections: [], overflowSections: [] })),
	);

	const coverages = events.map((e) => expandEvent(e, year, tz));

	const fullDayNonProd = new Set<string>();
	for (const c of coverages) {
		if (c.event.EventTypeCode === "P") continue;
		for (const { m, d, fullDay } of c.dayKeys) {
			if (fullDay) fullDayNonProd.add(`${m}-${d}`);
		}
	}

	for (const c of coverages) {
		const isProd = c.event.EventTypeCode === "P";
		for (const { m, d } of c.dayKeys) {
			if (isProd && fullDayNonProd.has(`${m}-${d}`)) continue;

			const cell = grid[m][d];
			if (cell.sections.some((s) => s.key === c.item.key)) continue;

			if (cell.sections.length < MAX_SECTIONS_PER_CELL) {
				cell.sections.push(c.item);
			} else {
				const { color, ...overflow } = c.item;
				cell.overflowSections.push(overflow);
			}
		}
	}

	return grid;
};

export type MonthStatus = "past" | "current" | "future";
export type MonthSummary = {
	scheduledFinishedTons: number;
	scheduledChargedTons: number;
	scheduledRolledTons: number;
	scheduledRollingHours: number;
	actualFinishedTons: number;
	actualChargedTons: number;
	actualRolledTons: number;
	actualRollingHours: number;
};

export const getMonthStatus = (
	year: number,
	monthIdx: number,
	todayYear: number,
	todayMonth: number,
): MonthStatus => {
	if (year < todayYear) return "past";
	if (year > todayYear) return "future";
	if (monthIdx < todayMonth) return "past";
	if (monthIdx > todayMonth) return "future";
	return "current";
};

export const safeRatio = (
	numerator: number | null,
	denominator: number | null,
) => {
	if (numerator == null || denominator == null || denominator === 0)
		return null;
	return numerator / denominator;
};

export const formatTph = (value: number | null) =>
	value == null
		? "—"
		: `${value.toLocaleString("en-US", { maximumFractionDigits: 1 })} t/h`;

export const formatYield = (value: number | null) =>
	value == null ? "—" : `${(value * 100).toFixed(1)}%`;

export const formatTons = (value: number) =>
	`${value.toLocaleString("en-US", { maximumFractionDigits: 0 })} tons`;

export const computeMonthSummary = (monthCells: DayCell[]): MonthSummary => {
	const seen = new Set<number>();
	const totals: MonthSummary = {
		scheduledFinishedTons: 0,
		scheduledChargedTons: 0,
		scheduledRolledTons: 0,
		scheduledRollingHours: 0,
		actualFinishedTons: 0,
		actualChargedTons: 0,
		actualRolledTons: 0,
		actualRollingHours: 0,
	};
	for (const cell of monthCells) {
		for (const item of [...cell.sections, ...cell.overflowSections]) {
			if (item.eventTypeCode !== "P") continue;
			if (seen.has(item.eventId)) continue;
			seen.add(item.eventId);
			totals.scheduledFinishedTons += item.scheduledFinishedTons ?? 0;
			totals.scheduledChargedTons += item.scheduledChargedTons ?? 0;
			totals.scheduledRolledTons += item.scheduledRolledTons ?? 0;
			totals.scheduledRollingHours += item.scheduledRollingHours ?? 0;
			totals.actualFinishedTons += item.actualFinishedTons ?? 0;
			totals.actualChargedTons += item.actualChargedTons ?? 0;
			totals.actualRolledTons += item.actualRolledTons ?? 0;
			totals.actualRollingHours += item.actualRollingHours ?? 0;
		}
	}
	return totals;
};
